import { Button, TextField, Typography } from "@material-ui/core";
import React, { Fragment, useContext, useState } from "react";
import useModal from "../hooks/useModal";
import SectionHeaderText from "./Display/SectionHeaderText";
import { DateTime } from "luxon";
import { WorkItemContext } from "../context/WorkItemContext";
import { useDistrictServiceDetails } from "../hooks/useDistrictServiceDetails";
import { RangeDatePicker } from "./RangeDatePicker/RangeDatePicker";
import Column from "./Display/Column";
import Toaster, { ToastType } from "../common/Toaster";
import FileSaver from "file-saver";

function AnimatedEllipsis() {
  const [dots, setDots] = React.useState("");

  React.useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => (prev.length < 3 ? prev + "." : ""));
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Using a fixed width span prevents the text from "jittering" as dots are added
  return <span style={{ display: "inline-block", width: "20px" }}>{dots}</span>;
}

function ProgressBar() {
  return (
    <>
      <Column style={{ paddingLeft: "20px", minWidth: "300px" }} gap="m">
        <Typography variant="caption" color="textSecondary">
          Querying records
          <AnimatedEllipsis />
        </Typography>
      </Column>
    </>
  );
}

export function DistrictExportControls() {
  // We need two distinct modal instances if useModal uses internal state
  const { modal: ConfigModal, toggle: toggleConfig } = useModal();

  const { thisWeek, timeRanges } = useContext(WorkItemContext);
  const [inputValue, setInputValue] = useState<string>("");
  const [start, setStart] = useState<DateTime>(thisWeek.start);
  const [end, setEnd] = useState<DateTime>(thisWeek.end);

  const { downloadCSV } = useDistrictServiceDetails();
  const [isExporting, setIsExporting] = useState(false);
  const handleFinish = () => {
    setIsExporting(false);
    setInputValue("");
    setStart(thisWeek.start);
    setEnd(thisWeek.end);
    toggleConfig();
  };

  const onProgress = React.useCallback((status: any) => {
    if (status.data && !status.file) {
      console.log(status.data);
    }

    if (status.file) {
      FileSaver.saveAs(new Blob([status.data]), status.file);
      Toaster(`Downloaded district service details`, ToastType.good);
    }
  }, []);

  return (
    <Fragment>
      <Button
        variant="outlined"
        color="primary"
        fullWidth
        onClick={toggleConfig}
      >
        District Service Details
      </Button>
      <ConfigModal onClose={toggleConfig}>
        <Column style={{ padding: "10px" }} gap="m">
          <SectionHeaderText>
            Download District Service Details
          </SectionHeaderText>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Exports all service records associated with <strong>all</strong>{" "}
            schools matching the <strong>queried prefix</strong>.
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Search is <strong>case insensitive</strong>.
          </Typography>
          <Typography
            variant="caption"
            color="primary"
            style={{ fontStyle: "italic", marginBottom: "5px" }}
          >
            e.g <strong>&quot;PLYMOUTH REGIONAL&quot;</strong> matches all{" "}
            <strong>Plymouth Regional High School</strong> districts.
          </Typography>

          <TextField
            variant="outlined"
            label="District (Prefix)"
            value={inputValue}
            onChange={(event) =>
              setInputValue(event.target.value.toLocaleUpperCase())
            }
          />

          <RangeDatePicker
            style={{ width: "100%" }}
            start={start}
            end={end}
            setStart={(start: DateTime | null) => {
              if (start) {
                setStart(start);
              }
            }}
            setEnd={(end: DateTime | null) => {
              if (end) {
                setEnd(end);
              }
            }}
            predefinedOptions={Array.from(timeRanges.values())}
          />
          {isExporting && <ProgressBar />}
          <Button
            color="primary"
            variant="outlined"
            disabled={!inputValue || isExporting}
            onClick={async () => {
              setIsExporting(true);

              await downloadCSV({
                school: { prefix: inputValue },
                start,
                end,
                onProgress,
              });

              handleFinish();
            }}
          >
            Download
          </Button>
        </Column>
      </ConfigModal>
    </Fragment>
  );
}
